import { useQuotaRequestMore } from '@/hooks'
import { useEntitlements } from '@/lib'
import { Button, logger } from '@/ui'
import { Tooltip } from '@/ui/rapid-ui/tooltip'
import type { StripeFeature } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import confetti from 'canvas-confetti'
import { CheckCircleIcon, CheckIcon, FlaskConicalIcon, SparklesIcon } from 'lucide-react'
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type GateMechanism =
	| { type: 'stripe'; feature: StripeFeature }
	| { type: 'early-access'; featureKey: string }
	| {
		/**
		 * Controlled gate: the caller owns the unlock state. For access that is granted
		 * out-of-band (e.g. a backend flag flipped by ops after a request), rather than
		 * by a Stripe entitlement or a self-serve PostHog flag.
		 */
		type: 'request-access'
		allowed: boolean
		requested: boolean
		onRequest: () => void
		isRequesting?: boolean
		ctaLabel: string
		/** Message shown in place of the CTA once access has been requested. */
		requestedLabel: string
	}

type GatePreviewProps = GateMechanism & {
	icon: ReactNode
	title: string
	description: string
	benefits: string[]
	/** Footer hint below the CTA. Falls back to a type-specific default when omitted. */
	hint?: ReactNode
	/** Force-allow the gated content regardless of the underlying check. */
	bypass?: boolean
}

export type GateProps = GatePreviewProps & {
	children: ReactNode
}

export function Gate(props: GateProps) {
	const allowed = useGateAllowed(props)
	if (allowed === undefined) { return null }
	if (allowed) { return <>{props.children}</> }
	return <GatePreview {...props} />
}

function useGateAllowed(props: GatePreviewProps): boolean | undefined {
	const { can, isLoading: stripeLoading } = useEntitlements()
	const earlyAccessKey = props.type === 'early-access' ? props.featureKey : ''
	const earlyAccessEnabled = useFeatureFlagEnabled(earlyAccessKey) ?? false

	if (props.bypass) { return true }
	if (props.type === 'stripe') {
		if (stripeLoading) { return undefined }
		return can(props.feature)
	}
	if (props.type === 'request-access') {
		return props.allowed
	}
	return earlyAccessEnabled
}

function gateFeatureId(props: GatePreviewProps): string {
	switch (props.type) {
		case 'stripe':
			return props.feature
		case 'early-access':
			return props.featureKey
		case 'request-access':
			return 'request-access'
	}
}

function GatePreview(props: GatePreviewProps) {
	const { t } = useTranslation('common')
	const featureId = gateFeatureId(props)

	const hint = props.hint
		?? (props.type === 'stripe'
			? t('entitlements.preview.upgradeHint')
			: props.type === 'early-access'
			? t('entitlements.preview.earlyAccessHint')
			: null)

	useEffect(() => {
		logger.track('Gate: Preview shown', {
			data: { gate_type: props.type, feature: featureId },
		})
	}, [props.type, featureId])

	return (
		<div className="flex flex-col items-center justify-center py-16 px-6">
			<div className="max-w-lg w-full bg-white border px-8 py-8 rounded-md">
				<div className="flex flex-col items-center text-center mb-6">
					<div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-50 text-brand-600 mb-4">
						{props.icon}
					</div>
					{props.type === 'stripe'
						? <UpgradeBadge />
						: props.type === 'early-access'
						? (
							<div className="mb-3">
								<EarlyAccessBadge />
							</div>
						)
						: null}
					<h2 className="text-xl font-semibold text-grey-900">{props.title}</h2>
					<p className="text-sm text-grey-500 mt-1.5">{props.description}</p>
				</div>

				<div className="bg-grey-50 rounded-xl p-5 mb-6">
					<ul className="space-y-3">
						{props.benefits.map((benefit) => (
							<li key={benefit} className="flex items-start gap-2.5 text-sm text-grey-700">
								<CheckIcon className="size-4 text-green-500 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
								{benefit}
							</li>
						))}
					</ul>
				</div>

				<div className="flex flex-col items-center gap-6">
					{props.type === 'stripe'
						? <RequestUpgradeCta feature={props.feature} />
						: props.type === 'early-access'
						? <EnrollEarlyAccessCta featureKey={props.featureKey} />
						: (
							<RequestAccessCta
								requested={props.requested}
								onRequest={props.onRequest}
								isRequesting={props.isRequesting}
								ctaLabel={props.ctaLabel}
								requestedLabel={props.requestedLabel}
							/>
						)}
					{hint ? <p className="text-xs text-grey-400">{hint}</p> : null}
				</div>
			</div>
		</div>
	)
}

function UpgradeBadge() {
	const { t } = useTranslation('common')
	return (
		<div
			className={ctl(`
				inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium
				bg-brand-50 text-brand-700 rounded-sm mb-3
			`)}
		>
			<SparklesIcon className="size-3" />
			{t('entitlements.preview.upgradeRequired')}
		</div>
	)
}

function RequestUpgradeCta({ feature }: { feature: StripeFeature }) {
	const { t } = useTranslation('common')
	const upgradeRequest = useQuotaRequestMore(null, `upgrade-request:${feature}`)

	const handleClick = () => {
		logger.track('Gate: Upgrade requested', { data: { feature } })
		upgradeRequest.request()
	}

	return (
		<Button
			type="colorful"
			size="short"
			onClick={handleClick}
			disabled={upgradeRequest.isDisabled}
			isLoading={upgradeRequest.isPending}
		>
			{upgradeRequest.isError
				? t('entitlements.requestUpgradeFailed')
				: upgradeRequest.hasSent
				? t('entitlements.requestUpgradeSent')
				: t('entitlements.requestUpgrade')}
		</Button>
	)
}

function EnrollEarlyAccessCta({ featureKey }: { featureKey: string }) {
	const { t } = useTranslation('common')
	const posthog = usePostHog()
	const [pending, setPending] = useState(false)
	const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		return () => {
			if (resetTimerRef.current) { clearTimeout(resetTimerRef.current) }
		}
	}, [])

	const handleEnroll = () => {
		logger.track('Gate: Early access enrolled', { data: { feature_key: featureKey } })
		setPending(true)

		try {
			posthog.updateEarlyAccessFeatureEnrollment(featureKey, true)
		} catch (error) {
			logger.error(error, { tags: { component: 'Gate', action: 'early-access-enroll' } })
			setPending(false)
			return
		}

		void confetti({
			particleCount: 120,
			spread: 80,
			origin: { y: 0.6 },
			colors: ['#b365f6', '#9a4bf5', '#872cf0', '#710ce2', '#cb87fb', '#f0cefd'],
		})

		// Enrollment is fire-and-forget: PostHog reloads flags asynchronously and, on
		// success, the flipped flag makes <Gate> unmount this preview. If the flag never
		// activates (enrollment failed), re-enable the CTA so the user can retry rather
		// than being stuck on a permanently disabled button.
		if (resetTimerRef.current) { clearTimeout(resetTimerRef.current) }
		resetTimerRef.current = setTimeout(() => setPending(false), 4000)
	}

	return (
		<Button type="colorful" size="short" onClick={handleEnroll} disabled={pending} isLoading={pending}>
			{pending
				? t('entitlements.preview.earlyAccessEnrolled')
				: t('entitlements.preview.earlyAccessCta')}
		</Button>
	)
}

function RequestAccessCta({
	requested,
	onRequest,
	isRequesting = false,
	ctaLabel,
	requestedLabel,
}: {
	requested: boolean
	onRequest: () => void
	isRequesting?: boolean
	ctaLabel: string
	requestedLabel: string
}) {
	if (requested) {
		return (
			<div className="flex items-start gap-2.5 text-sm text-grey-700">
				<CheckCircleIcon className="size-5 flex-shrink-0 text-green-600" />
				<p className="leading-relaxed">{requestedLabel}</p>
			</div>
		)
	}

	return (
		<Button type="committal" size="normal" onClick={onRequest} disabled={isRequesting} isLoading={isRequesting}>
			{ctaLabel}
		</Button>
	)
}

const BREWING_FLASK_STYLES = `
@keyframes flask-brew-bubble {
	0%   { transform: translate(var(--bx, 0px), 0) scale(0.4); opacity: 0; }
	25%  { opacity: 1; }
	75%  { opacity: 0.7; }
	100% { transform: translate(var(--bx, 0px), -9px) scale(0.3); opacity: 0; }
}
.flask-brew { position: relative; display: inline-flex; line-height: 0; }
.flask-brew-bubble {
	position: absolute;
	width: 2px;
	height: 2px;
	border-radius: 9999px;
	background: currentColor;
	pointer-events: none;
	animation: flask-brew-bubble 2.4s ease-in-out infinite;
	will-change: transform, opacity;
	z-index: 1;
}
`

export function BrewingFlaskIcon({ className }: { className?: string }) {
	return (
		<span className={`flask-brew ${className ?? 'w-3 h-3'}`}>
			<style>{BREWING_FLASK_STYLES}</style>
			<FlaskConicalIcon className="w-full h-full relative" />
			<span
				className="flask-brew-bubble"
				style={{ left: '4px', bottom: '2px', ['--bx' as string]: '-0.5px', animationDelay: '0s' }}
			/>
			<span
				className="flask-brew-bubble"
				style={{ left: '6px', bottom: '3px', ['--bx' as string]: '0.5px', animationDelay: '0.9s' }}
			/>
			<span
				className="flask-brew-bubble"
				style={{ left: '5px', bottom: '1.5px', ['--bx' as string]: '0px', animationDelay: '1.7s' }}
			/>
		</span>
	)
}

export function EarlyAccessBadge({ iconOnly = false }: { iconOnly?: boolean }) {
	const { t } = useTranslation('common')
	return (
		<Tooltip content={t('entitlements.earlyAccessBadgeTooltip')}>
			<span
				className={ctl(`
					inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium
					text-brand-700 rounded-sm cursor-help
					${iconOnly ? 'bg-transparent' : 'bg-brand-50'}
				`)}
			>
				<BrewingFlaskIcon />
				{!iconOnly && t('entitlements.preview.freeInBeta')}
			</span>
		</Tooltip>
	)
}
